import {useNavigate, useParams} from 'react-router-dom';
import useQuestionnaireNavigation from 'containers/NewQuestionnaire/hooks/useQuestionnaireNavigation';
import {useQueryParam} from 'utils/hooks';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {
  fetchQuestionnaireRequest,
  finishQuestionnaireStepRequest,
} from 'APIs/letrusAPI/questionnaire';
import {MissingQuestion} from 'APIs/letrusAPI/questionnaire/types';
import {useState} from 'react';
import LoadingQuestion from 'components/LoadingComponents/LoadingQuestion';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {updateUserProfileRequest} from 'APIs/letrusAPI/user';
import {useModals} from 'letrus-ui';
import styles from './Questionnaire.module.scss';
import QuestionnaireHeader from './components/QuestionnaireHeader';
import QuestionnaireFinishModal from './Modals/QuestionnaireFinishModal';
import MissingQuestionsModal from './Modals/MissingQuestionsModal';
import Question from './components/Question';
import Progressbar from './components/Progressbar';
import MatchQuestion from './components/MatchQuestion';
import AiTutorQuestion from './components/AiTutorQuestion';

function Questionnaire(): JSX.Element {
  const [prevQuestionAnswer, setPrevQuestionAnswer] = useState<string>('');
  const [missingQuestions, setMissingQuestions] = useState<MissingQuestion[]>(
    [],
  );
  const [isFinishing, setIsFinishing] = useState(false);

  const navigate = useNavigate();
  const queryParam = useQueryParam();
  const {questionnaire_id} = useParams();
  const learningPathUserInstanceId = queryParam.get('trilha') || undefined;

  const {finishModal, missingQuestionsModal} = useModals([
    'finishModal',
    'missingQuestionsModal',
  ]);

  const updateProfile = updateUserProfileRequest();
  const isSocioeconomicalQuestionnaire = questionnaire_id === '334';

  const {data: questionnaireData} = fetchQuestionnaireRequest(questionnaire_id);
  const questionnaire = useQuestionnaireNavigation(questionnaireData);

  const finishQuestionnaireStep = finishQuestionnaireStepRequest(
    learningPathUserInstanceId,
  );

  const {learningPath, navigateToLearningPath, getStepIdByActivity} =
    useLearningPathNavigation(learningPathUserInstanceId);

  // Prefer to extend the questionnaire and thequestion attempt instead of creating new endpoints
  // this way is easier to integrate new types of question
  const usesQuestionAttempt =
    questionnaire.currentStep?.question.question_type !== 'ai_tutor' &&
    questionnaire.currentStep?.question.question_type !== 'matching';

  const questionnaireOrder =
    learningPath?.steps.find((step) => {
      return step.activity_id === questionnaire.id;
    })?.order ?? 0;

  function goToNextQuestion(answer?: string) {
    if (answer) setPrevQuestionAnswer(answer);

    if (isSocioeconomicalQuestionnaire && questionnaire.isOnLastStep) {
      updateUserProfile();
    }

    return questionnaire.isOnLastStep
      ? finishModal?.showModal()
      : questionnaire.nextStep();
  }

  function finishQuestionnaire() {
    if (!learningPathUserInstanceId) {
      navigate('/');
      return;
    }

    setIsFinishing(true);

    finishQuestionnaireStep?.trigger(undefined, {
      onSuccess: () => {
        setIsFinishing(false);
        navigateToLearningPath?.nextStep(Number(questionnaire_id));
      },
      onError: (error) => {
        setIsFinishing(false);

        const responseData = error?.response?.data;

        if (
          error?.response?.status === 400 &&
          responseData?.missing_questions?.length
        ) {
          setMissingQuestions(responseData?.missing_questions);
          finishModal?.close();
          missingQuestionsModal?.showModal();
        } else {
          navigateToLearningPath?.nextStep(Number(questionnaire_id));
        }
      },
    });
  }

  function handleGoBackToMissingQuestion() {
    missingQuestionsModal?.close();

    if (missingQuestions?.length > 0) {
      const firstMissingQuestion = missingQuestions[0];

      questionnaire?.goToStepByOrder(firstMissingQuestion?.order);
    }
  }

  function handlePageReturn() {
    return learningPathUserInstanceId
      ? navigateToLearningPath.previousStep(Number(questionnaire_id))
      : navigate(-1);
  }

  function updateUserProfile() {
    const {userProfile} = getUserData();

    if (!userProfile?.answered_socioeconomical_questionnaire) {
      updateProfile.trigger({
        id: userProfile?.id,
        answered_socioeconomical_questionnaire: true,
      });
    }
  }

  return (
    <main className={styles.wrapper}>
      <QuestionnaireHeader
        name={questionnaire?.name}
        learningPath={learningPath}
        onGoBack={() => handlePageReturn()}
      />

      <section className={styles.questions}>
        <Progressbar
          amount={questionnaire.length}
          step={questionnaire.currentStep?.order || 0}
        />

        {!questionnaire.currentStep ? (
          <LoadingQuestion />
        ) : (
          <>
            {/* Questions that use questionnaire and question_attempts */}
            {usesQuestionAttempt && (
              <Question
                onAdvance={(answer) => goToNextQuestion(answer)}
                onReturn={() => questionnaire.previousStep()}
                question={questionnaire.currentStep?.question}
                isFirstQuestion={questionnaire.currentStep?.order === 1}
                learningPathStepId={getStepIdByActivity(questionnaire.id)}
              />
            )}

            {/* Question that don't use questionnaire and question_attempts */}
            {questionnaire.currentStep.question.question_type ===
              'matching' && (
              <MatchQuestion
                onReturn={() => questionnaire.previousStep()}
                onAdvance={(answer) => goToNextQuestion(answer)}
                question={questionnaire.currentStep?.question}
                isFirstQuestion={questionnaire.currentStep?.order === 1}
                learningPathStepId={getStepIdByActivity(questionnaire.id)}
              />
            )}

            {/* Question that don't use questionnaire and question_attempts */}
            {questionnaire.currentStep.question.question_type ===
              'ai_tutor' && (
              <AiTutorQuestion
                onAdvance={() => goToNextQuestion()}
                prevQuestionAnswer={prevQuestionAnswer}
                question={questionnaire.currentStep?.question}
                learningPathStepId={getStepIdByActivity(questionnaire.id)}
              />
            )}
          </>
        )}

        <QuestionnaireFinishModal
          id="finishModal"
          onClick={() => finishQuestionnaire()}
          isLoading={isFinishing}
          hasNextStep={
            questionnaire.currentStep?.question.question_type !== 'ai_tutor'
          }
          isLastLearningPathStep={
            questionnaireOrder === learningPath?.steps.length
          }
        />

        <MissingQuestionsModal
          id="missingQuestionsModal"
          missingQuestions={missingQuestions}
          onGoBack={handleGoBackToMissingQuestion}
        />
      </section>
    </main>
  );
}

export default Questionnaire;
